import gsap from "gsap";
import {
  BoxGeometry,
  Color,
  Group,
  Line,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  Object3d,
  TextureLoader,
  Vector2,
  Vector3
} from 'three';
import {FBM, Perlin} from 'three-noise';
import {Line2} from 'three/examples/jsm/lines/Line2.js';
import {LineGeometry} from 'three/examples/jsm/lines/LineGeometry.js';
import {LineMaterial} from 'three/examples/jsm/lines/LineMaterial.js';

// import Boid from './Boids.js';
import Creature from './Creature.js';

Math.degToRad = function(degrees) {
  var pi = Math.PI;
  return degrees * (pi / 180);
};

const getRandomNum = (max = 0, min = 0) =>
    Math.floor(Math.random() * (max + 1 - min)) + min;

const keyToReset = {
  "default" : "default"
};

const animationLookUp = {
  "default" : {
    "reset" : "default",
    "animation" : "default",
    "sprite" : "sprite-04.png"
  },
  "moods" : {
    "reset" : "falling",
    "animation" : "falling",
    "sprite" : "sprite-rose-02.png"
  },
  "stream" :
      {"reset" : "stream", "animation" : "stream", "sprite" : "sprite_cloud_"},
  "sword" : null,
  "river" : null,
  "heron" : null,
  "cemetery" : {
    "reset" : "fireflies",
    "animation" : "fireflies",
    "sprite" : "sprite-firefly-01.png"
  },
  "love" : {
    "reset" : "stars",
    "animation" : "stars",
    "sprite" : "sprite-star-04.png"
  },
  "crow" :
      {"reset" : "crow", "animation" : "crow", "sprite" : "sprite_feather.png"},
  "wind" : null,
  "pond" : {
    "reset" : "default",
    "animation" : "default",
    "sprite" : "sprite-04.png"
  },
  "rain-pine" : null,
  "poppies" :
      {"reset" : "poppies", "animation" : "poppies", "sprite" : "poppie.png"},
  "iris" : {
    "reset" : "flowers",
    "animation" : "flowers",
    "sprite" : "sprite-iris-02.png"
  },
  "cold-flower" : {
    "reset" : "cold-flower",
    "animation" : "cold-flower",
    "sprite" : "blue-flower-sprite-02.png"
  },
  "spider" : {"reset" : "spider", "animation" : "spider", "sprite" : ""},
  "well" : {"reset" : "well", "animation" : "well", "sprite" : "sprite_well_"}
};

export default class ParticleAnimations {

  constructor() {
    this.animating = false;
    this.particles = [];
    this.groundY = -.9;
  }

  setGround(ground) {
    this.groundY = ground;
    if (this.userHitAreaFloor) {
      this.userHitAreaFloor.position.y = ground;
    }
  }

  init(scene) {
    this.scene = scene;
    this.perlin = new Perlin(Math.random());
    this.particles = [];
    this.lines = [];
    this.creatureMeshGroup = new Group();
    this.linesMeshGroup = new Group();

    this.maxCreatureNum = 100;

    for (let i = 0; i < 500; i++) {
      const creature = new Creature();
      creature.state = "dead";
      this.creatureMeshGroup.add(creature.mesh);
      this.particles.push(creature);
    }

    this.scene.add(this.creatureMeshGroup);
    this.scene.add(this.linesMeshGroup);

    this.userHitAreas = [];
    const geometry = new BoxGeometry(4, 4, .1);
    const material = new MeshBasicMaterial(
        {color : 0x00ff00, transparent : true, opacity : .1});
    this.userHitArea = new Mesh(geometry, material);
    this.userHitArea.layers.enable(1);
    this.userHitArea.visible = false;
    this.userHitArea.name = "wall";
    this.userHitAreas.push(this.userHitArea);
    this.scene.add(this.userHitArea);

    const geometryF = new BoxGeometry(6, .1, 4);
    const materialF = new MeshBasicMaterial(
        {color : 0x00ff00, transparent : true, opacity : .25});
    this.userHitAreaFloor = new Mesh(geometryF, materialF);
    this.userHitAreaFloor.position.set(0, this.groundY, -2.25);
    this.userHitAreaFloor.visible = false;
    this.userHitAreaFloor.name = "floor";
    this.userHitAreas.push(this.userHitAreaFloor);
    this.scene.add(this.userHitAreaFloor);
  }

  clearParticles() {
    if (this.interval)
      clearInterval(this.interval);
    this.particles.forEach(
        (particle, i) => { this.cleanUpParticle(particle); });
  }

  clearParticlesSmooth() {

    this.key = null;
    if (this.particles.length == 0) {
      return;
    }
    if (this.interval)
      clearInterval(this.interval);

    if (this.spiderTM)
      this.spiderTM.pause();

    this.particles.forEach((particle, i) => {
      if (particle.tm) {
        clearTimeout(particle.tm);
        particle.tm = null;
      }

      if (particle.state != "dead") {

        this.killTweenForParticle(particle, particle.aOpacity);
        this.killTweenForParticle(particle, particle.aScale);
        this.killTweenForParticle(particle, particle.aVel);
        this.killTweenForParticle(particle, particle.aTarget);

        particle.aOpacity = gsap.to(particle.mesh.material, {
          opacity : 0,
          duration : .25,
          overwrite : true,
          onComplete : function() { this.cleanUpParticle(particle); }.bind(this)
        });

      } else {
        this.cleanUpParticle(particle);
      }
    });

    if (this.lines.length > 0) {
      this.lines.forEach((line, i) => {
        if (line.state != "dead") {
          gsap.to(line.material, {
            opacity : 0,
            duration : .5,
            overwrite : true,
            onComplete : function() { this.cleanUpLine(line); }.bind(this)
          });
        } else {
          this.cleanUpLine(line);
        }
      });
    }

    this.linesMeshGroup.visible = false;
  }

  cleanUpParticle(particle) {

    particle.state = "dead";

    this.killTweenForParticle(particle, particle.aOpacity);
    this.killTweenForParticle(particle, particle.aScale);
    this.killTweenForParticle(particle, particle.aVel);
    this.killTweenForParticle(particle, particle.aTarget);

    if (particle.interval) {
      clearInterval(particle.interval);
    }

    if (particle.tm) {
      clearTimeout(particle.tm);
      particle.tm = null;
    }
    particle.mesh.material.opacity = 0;
  }

  cleanUpLine(line) {
    line.state = "dead";
    line.material.opacity = 0;
    line.visible = false;

    let positions = [ 0, 0, 0, 0, 0, 0, 0, 0, 0 ];
    line.geometry.setPositions(positions);
    line.geometry.attributes.position.needsUpdate = true;
  }

  //-----
  getAnimationType(key) {
    if (key in animationLookUp) {
      return animationLookUp[key];
    } else {
      console.log("no key found", key);
      return null;
    }
  }

  //-----
  async initParticles(key, activate = true, vidTime = 0) {
    this.creatureMeshGroup.position.set(0, 0, 0);
    this.creatureMeshGroup.rotation.set(0, 0, 0);
    this.linesMeshGroup.position.set(0, 0, 0);
    this.linesMeshGroup.rotation.set(0, 0, 0);
    this.maxCreatureNum = 100;
    this.userHitArea.position.set(0, 0, 0);
    this.userHitAreaFloor.position.set(0, this.groundY, -2.25);

    if (this.interval) {
      clearInterval(this.interval);
    }
    this.key = key;
    let animationData = this.getAnimationType(key);
    if (animationData == null)
      return;

    let animationType = animationData.animation;

    if (animationType == "default") {
      this.initParticlesDefault(activate);
    } else if (animationType == "falling") {

      const map = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite);

      this.initFallingParticles(map, activate);

      this.animating = true;

    } else if (animationType == "fireflies") {
      const map = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite);

      this.initFirefliesParticles(map, activate);

      this.animating = true;
    } else if (animationType == "flowers") {
      this.linesMeshGroup.visible = true;

      const map = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite);

      this.initFlowersParticles(map, activate);

      this.animating = true;
    } else if (animationType == "stars") {
      const map = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite);

      this.initStarsParticles(map, activate);

      this.animating = true;
    } else if (animationType == "poppies") {
      this.linesMeshGroup.visible = true;

      const map = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite);

      this.initPoppiesParticles(map, activate, vidTime);

      this.animating = true;
    } else if (animationType == "spider") {
      this.linesMeshGroup.visible = true;

      // const map = await new TextureLoader().loadAsync(
      //     '/haiku-visualization/assets/images/' + animationData.sprite);

      this.initSpiderParticles(activate);

      this.animating = true;
    } else if (animationType == "cold-flower") {
      this.linesMeshGroup.visible = true;

      const map = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite);
      this.initColdFlowersParticles(map, activate);
      this.animating = true;
    } else if (animationType == "spider") {
      this.linesMeshGroup.visible = true;

      // const map = await new TextureLoader().loadAsync(
      //     '/haiku-visualization/assets/images/' + animationData.sprite);

      this.initSpiderParticles(activate);

      this.animating = true;
    } else if (animationType == "crow") {
      const map = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite);
      this.initCrowParticles(map, activate);
      this.animating = true;
    } else if (animationType == "well") {
      const maps = [];
      maps[0] = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite +
          "0.png");
      maps[1] = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite +
          "1.png");
      maps[2] = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite +
          "2.png");
      this.initWellParticles(maps, activate);
      this.animating = true;
    } else if (animationType == "stream") {
      const maps = [];

      maps[0] = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite +
          "0.png");
      maps[1] = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite +
          "1.png");
      maps[2] = await new TextureLoader().loadAsync(
          '/haiku-visualization/assets/images/' + animationData.sprite +
          "2.png");
      // maps[1] = await new TextureLoader().loadAsync(
      //     '/haiku-visualization/assets/images/' + animationData.sprite +
      //     "1.png");
      // maps[2] = await new TextureLoader().loadAsync(
      //     '/haiku-visualization/assets/images/' + animationData.sprite +
      //     "2.png");
      this.initCloudParticles(maps, activate);
      this.animating = true;
    }
  }

  initCloudParticles(maps, activate) {
    let animationData = this.getAnimationType(this.key);
    if (animationData == null)
      return;
    this.maxCreatureNum = 20;
    this.cloudsOn = false;
    this.particles.forEach((particle, i) => {
      if (i < this.maxCreatureNum) {
        particle.mesh.material.map = maps[i % 3];
        this.resetParticle(particle, animationData.reset, i);
        // particle.velocity.x = .1;
        particle.state = "active";
      } else {
        particle.mesh.material.opacity = 0;
        particle.state = "dead";
      }
    });
  }

  initWellParticles(maps, activate) {
    this.userHitArea.position.set(0, 0, -10);
    this.userHitAreaFloor.position.set(0, this.groundY, -1.5);

    // this.userHitAreaFloor.position.set(0, 0, -10);
    let animationData = this.getAnimationType(this.key);
    if (animationData == null)
      return;
    this.particles.forEach((particle, i) => {
      particle.mesh.material.map = maps[i % 3];
      this.resetParticle(particle, animationData.reset);
      if (activate && i < this.maxCreatureNum) {
        particle.mesh.visible = true;
      } else {
        particle.mesh.material.opacity = 0;
        particle.state = "dead";
      }
    });
  }

  initColdFlowersParticles(map, activate) {
    let animationData = this.getAnimationType(this.key);
    if (animationData == null)
      return;
    this.particles.forEach((particle, i) => {
      particle.mesh.material.map = map;
      this.resetParticle(particle, animationData.reset);

      if (activate && i < this.maxCreatureNum) {
        particle.mesh.visible = true;
      } else {
        particle.mesh.material.opacity = 0;
        particle.state = "dead";
        particle.position.set(Math.random() * 3 - 1,
                              (this.groundY + .01 + Math.random() * .015),
                              Math.random() - 1);
      }
    });
    this.makeLineMeshes();
    this.lines.forEach((line, i) => {
      if (i < 90) {
        const positions = []; // line.geometry.attributes.position.array;
        let index = i + this.maxCreatureNum;
        positions[0] = this.particles[index].position.x - .01;
        positions[1] = this.particles[index].position.y;
        positions[2] = this.particles[index].position.z;
        positions[3] = this.particles[index].position.x;
        positions[4] = this.groundY;
        positions[5] = this.particles[index].position.z;
        positions[6] = this.particles[index].position.x + .01;
        positions[7] = this.particles[index].position.y;
        positions[8] = this.particles[index].position.z;

        line.geometry.setPositions(positions);
        line.geometry.attributes.position.needsUpdate = true;
        line.visible = true;
        line.state = "dead";
        line.material.opacity = .5;

      } else {
        line.visible = false;
        line.state = "dead";
      }
    });
  }

  initPoppiesParticles(map, activate, vidTime) {
    let animationData = this.getAnimationType(this.key);
    if (animationData == null)
      return;
    this.didWind = false;
    this.particles.forEach((particle, i) => {
      particle.mesh.material.map = map;
      this.resetParticle(particle, animationData.reset);

      if (activate && i < this.maxCreatureNum) {

        particle.state = "ready";
        // let delay =
        //     (7.5 * (i + 1)) - vidTime; // Math.floor(Math.random() * 100);
        // let size = .09 + Math.random() * .06;
        // gsap.to(particle.mesh.material,
        //         {opacity : 1, duration : 1, delay : delay});
        // gsap.to(particle.mesh.scale, {
        //   x : size,
        //   y : size,
        //   z : size,
        //   duration : 1,
        //   delay : delay,
        //   onComplete : function() { particle.state = "active"; }.bind(this)
        // });

        // this.killTweenForParticle(particle, particle.aOpacity);

        // particle.aOpacity = gsap.to(particle.mesh.material,
        //                             {opacity : 1, duration : 2, delay : 0});
        // let size = .1 + Math.random() * .04;
        // particle.aScale =
        //     gsap.to(particle.mesh.scale,
        //             {x : size, y : size, z : size, duration : 2, delay : 0});
        // particle.tweens.push(particle.aOpacity);

      } else {
        particle.mesh.material.opacity = 0;
        particle.state = "dead";
      }
    });

    this.makeLineMeshes(.5);

    // this.interval =
    //     setInterval(function() { this.addWind(new Vector3(.1, .12, 0));
    //     }.bind(
    //                     this),
    //                 20000);
  }

  initFlowersParticles(map, activate) {

    let animationData = this.getAnimationType(this.key);
    if (animationData == null)
      return;

    this.particles.forEach((particle, i) => {
      particle.mesh.material.map = map;
      this.resetParticle(particle, animationData.reset);

      if (activate && i < this.maxCreatureNum) {

        particle.state = "active";

        this.killTweenForParticle(particle, particle.aOpacity);

        particle.aOpacity = gsap.to(particle.mesh.material,
                                    {opacity : 1, duration : 2, delay : 0});
        let size = .1 + Math.random() * .04;
        particle.aScale =
            gsap.to(particle.mesh.scale,
                    {x : size, y : size, z : size, duration : 2, delay : 0});
        particle.tweens.push(particle.aOpacity);

      } else {
        particle.mesh.material.opacity = 0;
        particle.state = "dead";
      }
    });

    this.makeLineMeshes();

    this.interval =
        setInterval(function() { this.addWind(new Vector3(.1, .12, 0)); }.bind(
                        this),
                    20000);
  }

  initFirefliesParticles(map, activate) {

    this.firefliesUser = false;
    this.fireflyfriction = .9;

    let animationData = this.getAnimationType(this.key);
    if (animationData == null)
      return;

    this.particles.forEach((particle, i) => {
      particle.mesh.material.map = map;
      this.resetParticle(particle, animationData.reset);

      if (activate && i < this.maxCreatureNum) {
        particle.state = "active";

        let x = (Math.random() - .5) * 3;
        let y = (Math.random() - .5) * 2;
        let z = (Math.random() - .75) * 3;
        particle.target.set(x, y, z);

        particle.interval = setInterval(function() {
          let x = (Math.random() - .5) * 5;
          let y = (Math.random() - .5) * 3;
          let z = (Math.random() - .5) * 5;
          if (!this.firefliesUser) {
            particle.aTarget =
                gsap.to(particle.target, {x : x, y : y, z : z, duration : 0});
          }
        }.bind(this), 8000);
      } else {
        particle.mesh.material.opacity = 0;
        particle.state = "dead";
      }
    });
  }

  initStarsParticles(map, activate) {

    let animationData = this.getAnimationType(this.key);
    if (animationData == null)
      return;

    this.particles.forEach((particle, i) => {
      particle.mesh.material.map = map;
      this.resetParticle(particle, animationData.reset);
      particle.state = "active";
      // if (activate && i < this.maxCreatureNum) {
      //   // particle.state = "active";
      // }
    });
  }

  initFallingParticles(map, activate) {

    this.userHitArea.position.set(0, 0, -1);

    let animationData = this.getAnimationType(this.key);
    if (animationData == null)
      return;

    this.particles.forEach((particle, i) => {
      particle.mesh.material.map = map;
      this.resetParticle(particle, animationData.reset);

      if (activate && i < this.maxCreatureNum) {
        particle.state = "dead";

        particle.tm = setTimeout(function() {
          particle.state = "active";

          particle.aOpacity = gsap.to(particle.mesh.material, {
            opacity : 1 - (-particle.position.z / 2),
            duration : 2,
            delay : 0
          });

          particle.tweens.push(particle.aOpacity);

          particle.rotationRan = 1 + (Math.random() * .001);
        }.bind(this), 2000 + Math.random() * 200000);
      } else {
        particle.mesh.material.opacity = 0;
        particle.state = "dead";
      }
    });
  }

  initSpiderParticles(activate) {
    let animationData = this.getAnimationType(this.key);
    if (animationData == null)
      return;

    // this.linesMeshGroup.rotation.y = Math.degToRad(-2);
    this.linesMeshGroup.rotation.x = Math.degToRad(-90);
    this.linesMeshGroup.position.x = .65;
    this.linesMeshGroup.position.y = -.99;
    this.linesMeshGroup.position.z = -.25;
    this.maxCreatureNum = 300;

    this.particles.forEach((particle, i) => {
      particle.mesh.material.opacity = 0;
      this.resetParticle(particle, animationData.reset, i);

      if (activate && i < this.maxCreatureNum) {

        particle.state = "dead";
        particle.position.set(0, 0, 0);

      } else {
        particle.mesh.material.opacity = 0;
        particle.state = "dead";
      }
    });

    this.makeLineMeshes(0);

    // if (!this.spiderTM) {
    this.makeSpiderTimeline();
    // } else {
    // this.spiderTM.restart();
    // }
  }

  makeSpiderTimeline() {
    this.spiderTM =
        gsap.timeline({repeat : -1, repeatDelay : 5, repeatRefresh : true});

    let pTM = gsap.timeline();
    this.particles.forEach((particle, i) => {
      let tm = gsap.timeline();

      tm.to(
          particle.position,
          {
            x : particle.opos.x,
            y : particle.opos.y,
            z : particle.opos.z,
            duration : 3,
            ease : "sine.inOut"
          },
      );
      pTM.add(tm, "<+=.01");
    });

    this.spiderTM.add(pTM);

    let lTM = gsap.timeline();
    this.lines.forEach((line, i) => {
      let op = .25;
      if (i > 200) {
        op *= ((300 - i) / 100);
      }
      let tm = gsap.timeline();
      tm.to(
          line.material,
          {opacity : op, duration : 0, ease : "sine.inOut"},
      );
      tm.to(line.material, {opacity : 0, duration : 1, ease : "sine.inOut"},
            "<+=8");
      lTM.add(tm, "<+=.01");
    });

    this.spiderTM.add(lTM, "<");

    let pTM2 = gsap.timeline();
    this.particles.forEach((particle, i) => {
      let tm = gsap.timeline();
      tm.to(
          particle.position,
          {x : 0, y : 0, z : 0, duration : 0, ease : "sine.inOut"},
      );

      pTM2.add(tm, "<+=.01");
    });

    this.spiderTM.add(pTM2);

    var random = function() {
      return gsap.utils.random(Math.degToRad(-90), Math.degToRad(0));
    };

    this.spiderTM.to(this.linesMeshGroup.rotation, {duration : 0, x : random});

    this.spiderTM.to(this.linesMeshGroup.position, {
      duration : 0,
      x : () => { return gsap.utils.random(-1, 2) },
      y : () => { return gsap.utils.random(-1, 1) },
      z : () => { return gsap.utils.random(-.25, -1) },
      duration : 0
    });
  }

  initCrowParticles(map, activate) {

    let animationData = this.getAnimationType(this.key);
    if (animationData == null)
      return;

    this.particles.forEach((particle, i) => {
      particle.mesh.material.map = map;
      this.resetParticle(particle, animationData.reset);
      particle.state = "dead";
    });

    this.doCrowInteraction(new Vector3(-1, 1, -1));
  }

  async initParticlesDefault(activate = true) {
    this.key = "default";

    const map = await new TextureLoader().loadAsync(
        '/haiku-visualization/assets/images/sprite-04.png');

    this.particles.forEach((particle, i) => {
      particle.mesh.material.map = map;
      this.resetParticle(particle);

      if (activate && i < this.maxCreatureNum) {
        particle.state = "active";
        this.killTweenForParticle(particle, particle.aOpacity);
        particle.aOpacity =
            gsap.to(particle.mesh.material,
                    {opacity : .5, duration : 2, delay : Math.random() * 3});
        particle.tweens.push(particle.aOpacity);
      } else {
        particle.mesh.material.opacity = 0;
        particle.state = "dead";
      }
    });
    this.animating = true;
  }

  makeLineMeshes(startOpacity = .5) {

    if (this.lines.length == 0) {

      for (let i = 0; i < this.maxCreatureNum; i++) {
        const geometry = new LineGeometry();
        let positions = this.getLinePositions(i);

        geometry.setPositions(positions);

        let matLine = new LineMaterial({
          color : 0x98b27f,
          linewidth : .001,
          dashed : false,
          alphaToCoverage : true,
          transparent : true,
          opacity : startOpacity,
          depthWrite : false
        });

        let line = new Line(geometry, matLine);
        line.scale.set(1, 1, 1);
        line.renderOrder = -1;
        line.state = "active";
        this.linesMeshGroup.add(line);
        this.lines.push(line);
        line.material.opacity = startOpacity;
      }
    } else {
      this.lines.forEach((line, i) => {
        if (i < this.maxCreatureNum) {
          line.state = "active";
          line.material.opacity = startOpacity;
          line.visible = true;

        } else {
          line.state = "dead";
          line.material.opacity = 0;
          line.visible = false;
        }
      });
    }

    if (this.key == "poppies")
      this.updateLineMeshes();
  }

  getLinePositions(i) {
    if (this.key == "poppies" || this.key == "iris") {
      return this.getLinePositionsFlowers(i);
    } else {
      return this.getLinePositionsFlowers(i);
    }
  }

  getLinePositionsFlowers(i) {
    let positions = [];
    positions.push(this.particles[i].position.x - .01,
                   this.particles[i].position.y, this.particles[i].position.z);
    positions.push(this.particles[i].opos.x, -1, this.particles[i].opos.z);
    positions.push(this.particles[i].position.x + .01,
                   this.particles[i].position.y, this.particles[i].position.z);
    return positions;
  }

  //----
  resetParticle(particle, resetType = "default", index = null) {

    particle.velocity.set(0, 0, 0);
    particle.acceleration.set(0, 0, 0);
    particle.mesh.visible = true;
    particle.mesh.material.opacity = 1;

    if (resetType == "default") {
      const radius = getRandomNum(.1, 1);
      const theta = Math.degToRad(getRandomNum(180));
      const phi = Math.degToRad(getRandomNum(360));
      particle.position.x = Math.sin(theta) * Math.cos(phi) * radius * 4;
      particle.position.y = Math.sin(theta) * Math.sin(phi) * radius;
      particle.position.z = Math.cos(theta) * radius - 1.0;
      particle.mesh.position.copy(particle.position);
      particle.mesh.scale.set(.10, .10, .10);
      particle.velocity.x = (Math.random() - .5) * 0.01;
      particle.velocity.y = (Math.random()) * 0.01;
      particle.velocity.z = -(Math.random() - .5) * 0.001;
      particle.velocity.normalize();
      particle.velocity.multiplyScalar(.0025);
      particle.friction = .9999;
      particle.mesh.material.opacity = 0;

    } else if (resetType == "falling") {
      particle.position.x = (Math.random() - .5) * .25;
      particle.position.y = 2;
      particle.position.z = (Math.random() - 1) * 2;
      particle.mesh.position.copy(particle.position);
      let size = .15 + Math.random() * .1;
      particle.mesh.scale.set(size, size, size);
      particle.velocity.x = (Math.random() - .5) * 0.01;
      particle.velocity.y = 0;
      particle.velocity.z = -(Math.random() - .5) * 0.001;
      particle.velocity.normalize();
      particle.velocity.multiplyScalar(.0025);
      particle.friction = .9999;
      particle.mesh.material.opacity = 0;

    } else if (resetType == "fireflies") {
      particle.position.x = (Math.random() - .5) * 3;
      particle.position.y = (Math.random() - .5) * 2;
      particle.position.z = (Math.random() - .75) * 3;
      particle.mesh.position.copy(particle.position);
      particle.mesh.scale.set(.075, .075, .075);
      particle.velocity.set(0, 0, 0);
      particle.acceleration.set(0, 0, 0);
      particle.mesh.material.opacity = 1;
      particle.opos.copy(particle.position);
      particle.friction = .9999;

    } else if (resetType == "flowers") {
      let radius = Math.random() * 2; //(0, 1);
      const theta = Math.degToRad(Math.random() * 360);
      const phi = Math.degToRad(getRandomNum(360));
      particle.position.x = Math.sin(theta) * radius;
      particle.position.y = (this.groundY + .1) - (Math.random() * .01);
      particle.position.z = (Math.cos(theta) * radius * .25) - .65;

      // portrait hack
      if (window.innerWidth < window.innerHeight) {
        particle.position.z -= 1;
      }

      particle.mesh.material.rotation = (Math.random() - .5) * (Math.PI / 2);
      particle.mesh.position.copy(particle.position);
      particle.mesh.scale.set(.1, .1, .1);
      particle.mesh.material.opacity = .75;
      particle.opos.copy(particle.position);
      particle.friction = .99;

    } else if (resetType == "poppies") {
      let radius = Math.random() * 2;
      const theta = Math.degToRad(Math.random() * 360);
      const phi = Math.degToRad(getRandomNum(360));
      particle.position.x = Math.sin(theta) * radius;
      particle.position.y = (this.groundY + .1) - (Math.random() * .01);
      particle.position.z = (Math.cos(theta) * radius * .25) - .65;

      if (window.innerWidth < window.innerHeight) {
        particle.position.z -= 1;
      }

      particle.mesh.material.rotation = (Math.random() - .5) * (Math.PI / 2);
      particle.mesh.position.copy(particle.position);
      particle.mesh.scale.set(.01, .01, .01);
      particle.mesh.material.opacity = .5;
      particle.opos.copy(particle.position);
      particle.friction = .99;

    } else if (resetType == "stars") {
      particle.position.x = (Math.random() - .5) * 10;
      particle.position.y = Math.random() * 5;
      particle.position.z = -Math.random() * 10;
      particle.mesh.position.copy(particle.position);
      particle.mesh.scale.set(.1, .1, .1);
      particle.mesh.material.opacity = 1;
      particle.opos.copy(particle.position);
    } else if (resetType == "spider") {
      let radius = index * .005 +
                   Math.random() * .01; // Math.random() * index; //(0, 1);
      var theta = Math.degToRad(
          index * 30 + (Math.random() - .5)); // Math.random() * 2.0 * Math.PI;
      let x = Math.cos(theta) * radius;
      let y = Math.sin(theta) * radius - (Math.random() * .01);
      let z = -Math.random() * .01; // - Math.random() * .1;
      particle.position.x = x;      // Math.cos(phi) * radius * 2;
      particle.position.y = y;      //-.9 - (Math.random() * .01);
      particle.position.z = z;      //(Math.cos(theta) * radius * .25) - .65;

      particle.mesh.position.copy(particle.position);
      particle.mesh.scale.set(.1, .1, .1);
      particle.mesh.material.opacity = 0;
      particle.opos.copy(particle.position);
      particle.target.set(x, y, z);

      if (index < 12) {
        let r2 = (this.maxCreatureNum) *
                 .01; // Math.floor(Math.random() * this.maxCreatureNum) * .01;
        x = Math.cos(theta) * r2;
        y = Math.sin(theta) * r2 - 4;
      }
      particle.target2 = new Vector3(x, y, z);
    } else if (resetType == "cold-flower") {
      particle.position.set(-1, 0, 0);
      particle.mesh.position.copy(particle.position);
      particle.mesh.scale.set(.1, .1, .1);
      particle.mesh.material.opacity = 0;
      particle.opos.copy(particle.position);
    } else if (resetType == "crow") {
      particle.position.set(0, 0, 0);
      particle.mesh.position.copy(particle.position);
      particle.mesh.scale.set(.25, .25, .25);
      particle.mesh.material.opacity = 0;
      particle.velocity.set(0, 0, 0);
      particle.opos.copy(particle.position);
    } else if (resetType == "well") {
      let radius = 1.5 + Math.random() * .25;
      const theta = Math.degToRad(Math.random() * 360);
      const phi = Math.degToRad(getRandomNum(360));
      particle.position.x = Math.sin(theta) * radius;
      particle.position.y = this.groundY - .01; // + (Math.random() * .01);
      particle.position.z = (Math.cos(theta) * radius * .25) - .65;
      particle.mesh.material.rotation = Math.random() * (2 * Math.PI);
      if (window.innerWidth < window.innerHeight) {
        particle.position.z -= 1;
      }

      // particle.mesh.material.rotation = (Math.random() - .5) * (Math.PI / 2);
      particle.mesh.position.copy(particle.position);

      let size = .08 + Math.random() * .04;
      particle.mesh.scale.set(size, size, size);
      particle.mesh.material.opacity = 1;
      particle.opos.copy(particle.position);
      // particle.friction = .99;
    } else if (resetType == "stream") {
      particle.position.x = (Math.random() * 10) - 5;
      particle.position.y = 1 + Math.random() - .5;
      particle.position.z = -1 - Math.random() * .1;
      particle.velocity.x = .05 + Math.random() * .2;
      particle.mesh.position.copy(particle.position);
      let scale = 1 + Math.random() * .25;
      particle.mesh.scale.set(scale, scale, scale);
      particle.mesh.material.opacity = 0;
      particle.mesh.visible = true;
      particle.opos.copy(particle.position);
      particle.friction = 1;
    }
  }

  //-----
  animate(dt, vidTime = 0) {
    if (!this.animating)
      return;

    if (!this.t)
      this.t = 0;
    this.t += dt;

    this.particles.forEach((particle, i) => {
      if (particle.state != "dead") {
        if (this.key != "love" && this.key != "stream")
          particle.update();
        this.update(particle, this.key, dt, this.t);
      } else if (this.key == "poppies" && i < this.maxCreatureNum) {
        particle.update();
      }
    });

    if (this.lines.length > 0) {
      this.updateLineMeshes();
    }

    if (this.key == "poppies") {

      if (vidTime < this.lastVidTime) {
        let dir = Math.floor((Math.random() - .5) * 2);
        if (!this.didWind) {
          this.addWind(
              new Vector3((.1 + (Math.random() - .5) * .001) * dir, 0, 0));
        }
        let count = 0;
        for (let i = 0; i < this.maxCreatureNum; i++) {

          if (this.particles[i].state == "ready" && count < 6) {
            this.particles[i].state = "active";
            let delay = count * .1;
            let particle = this.particles[i];
            let size = .09 + Math.random() * .06;
            gsap.to(particle.mesh.material,
                    {opacity : 1, duration : .5, delay : delay});
            gsap.to(
                particle.mesh.scale,
                {x : size, y : size, z : size, duration : 1, delay : delay});
            count++;
          }
          if (count >= 6) {
            break;
          }
        }
      }
      this.lastVidTime = vidTime;
    } else if (this.key == "love") {
      if (this.starsRotate) {
        this.creatureMeshGroup.rotation.y += dt * .025;
      }
    }
  }

  updateLineMeshes() {

    if (this.key == "iris" || this.key == "poppies") {
      this.lines.forEach((line, i) => {
        if (line.state == "active" || this.key == "poppies") {

          const positions = []; // line.geometry.attributes.position.array;
          positions[0] = this.particles[i].position.x - .01;
          positions[1] = this.particles[i].position.y;
          positions[2] = this.particles[i].position.z;
          positions[3] = this.particles[i].opos.x;
          positions[4] = this.groundY;
          positions[5] = this.particles[i].opos.z;
          positions[6] = this.particles[i].position.x + .01;
          positions[7] = this.particles[i].position.y;
          positions[8] = this.particles[i].position.z;

          line.geometry.setPositions(positions);
          line.geometry.attributes.position.needsUpdate = true;
          line.visible = true;
        }
      });
    } else if (this.key == "spider") {

      this.lines.forEach((line, i) => {
        if (i > 0) {
          this.particles[i].target.copy(this.particles[i - 1].position);
        } else {
          this.particles[i].target.copy(this.particles[i].position);
        }

        if (i < this.maxCreatureNum - 12) {
          this.particles[i].target2.copy(this.particles[i + 12].position);
        } else {
          this.particles[i].target2.copy(this.particles[i].position);
        }

        const positions = []; // line.geometry.attributes.position.array;
        positions[0] = this.particles[i].target.x;
        positions[1] = this.particles[i].target.y;
        positions[2] = this.particles[i].target.z;
        positions[3] = this.particles[i].position.x;
        positions[4] = this.particles[i].position.y;
        positions[5] = this.particles[i].position.z;
        positions[6] = this.particles[i].target2.x;
        positions[7] = this.particles[i].target2.y;
        positions[8] = this.particles[i].target2.z;
        line.geometry.setPositions(positions);
        line.geometry.attributes.position.needsUpdate = true;
        line.visible = true;
        // line.material.opacity = .5;
      });
    }
  }

  addWind(force) {
    // force.multiplyScalar(.0015);

    this.particles.forEach((particle, i) => {
      if (particle.state != "dead" || this.key == "poppies") {
        // ,particle.boost.copy(force);
        if (this.key != "poppies") {
          force.x += (Math.random() - .5) * .001;
          force.y += (Math.random() - .5) * .001;
          force.z += (Math.random() - .5) * .001;
        }

        force.normalize();
        force.multiplyScalar(.001);
        // particle.applyForce(force);
        let maxForce = .002;
        if (force.length() > maxForce) {
          force.clampLength(0, maxForce);
        }
        particle.boost.copy(force);
      }
    });
  }

  killTweenForParticle(particle, tween) {
    if (tween) {
      tween.kill();
      var index = particle.tweens.indexOf(tween);
      if (index != -1)
        particle.tweens.splice(index, 1);
    }
  }

  update(particle, key, dt, t) {

    if (key == "cemetery") {
      t *= .25;

      let noise = this.perlin.get2(new Vector2(particle.opos.x * t, t));
      particle.mesh.material.opacity = noise;
      particle.friction = this.fireflyfriction;
      this.attract(particle, particle.target, .0001);

    } else if (key == "stream") {
      particle.mesh.position.x += dt * particle.velocity.x;
      if (particle.mesh.position.x > 5) {
        particle.mesh.position.x = -5 - Math.random() * 5;
      }
    } else if (key == "iris" || key == "poppies") {
      this.attract(particle, particle.opos, .00001);

    } else if (key == "moods") {

      if (particle.position.y <= -.9 && particle.state != "sleep") {

        this.killTweenForParticle(particle, particle.aVel);

        particle.aVel =
            gsap.to(particle.velocity,
                    {x : 0, y : 0, z : 0, duration : 1, ease : 'sine.inOut'});

        particle.aOpacity =
            gsap.to(particle.mesh.material,
                    {opacity : 0, duration : 1, ease : 'sine.inOut'});

        particle.tweens.push(particle.aVel);

        particle.state = "sleep";

      } else {
        particle.mesh.material.rotation += dt * particle.rotationRan;
        particle.applyForce(new Vector3(0, -.00001, 0));
      }
    } else if (key == "love") {
      t *= .1;
      let noise = this.perlin.get3(new Vector3(
          particle.opos.x * t, particle.opos.y * t, particle.opos.z * t));
      // let noise = this.perlin.get2(new Vector2(particle.opos.x * t, t));
      particle.mesh.material.opacity = .25 + noise;
    } else if (key == "spider") {
      // this.attract(particle, particle.opos, .00001);
    }
  }

  attract(particle, target, speed) {

    let maxForce = .1;
    const toGoalVector = new Vector3();
    toGoalVector.subVectors(target, particle.position);
    const distance = toGoalVector.length();
    toGoalVector.normalize();
    toGoalVector.multiplyScalar(speed);
    // const steerVector = new Vector3();
    // steerVector.subVectors(toGoalVector, particle.velocity);
    // if (steerVector.length() > maxForce) {
    //   steerVector.clampLength(0, maxForce);
    // }

    particle.applyForce(toGoalVector);
  }

  onGroundChange(prevGround) {
    let gChange = prevGround - this.groundY;
    console.log("gChange", gChange, prevGround, this.groundY);
    if (this.key == "iris" || this.key == "poppies") {
      this.particles.forEach((item, i) => {
        if (i < this.maxCreatureNum) {
          this.particles[i].opos.y -= gChange;
          this.particles[i].position.y -= gChange;
          this.particles[i].mesh.position.copy(this.particles[i].position);
        }
      });
    }
  }
  //--- user interaction
  doUserDrag(intersects) {

    if (this.key == "cold-flower" || this.key == "default" ||
        this.key == "cemetery") {
      let hits = this.getHitPoints(intersects);
      if (this.key == "cold-flower" && hits.hitFloor) {
        // this.doColdFlowerInteraction(this.userHitPointFloor);
      } else if (this.key == "default" && hits.hitWall) {
        this.doPondInteraction(this.userHitPoint);
      } else if (this.key == "cemetery" && hits.hitWall) {
        this.doFirefliesInteraction(this.userHitPoint);
      }
    }
  }

  doUserClicked(intersects) {

    let hits = this.getHitPoints(intersects);

    console.log("doUserClicked", this.key);
    console.log("hits", hits.hitWall);
    switch (this.key) {
    case "moods":
      if (hits.hitWall)
        this.doRoseInteraction(this.userHitPoint);
      break;
    case "spider":
      if (hits.hitWall)
        this.doSpiderInteraction(this.userHitPoint);
      break;
    case "cold-flower":
      if (hits.hitFloor)
        this.doColdFlowerInteraction(this.userHitPointFloor);
      break;
    case "default":
      if (hits.hitWall)
        this.doPondInteraction(this.userHitPoint);
      break;
    case "love":
      if (hits.hitWall)
        this.doStarsInteraction();
      break;
    case "cemetery":
      // if (this.hitWall)
      // this.doFirefliesInteraction(this.userHitPoint);
      break;
    case "iris":
      if (hits.hitFloor)
        this.doIrisInteraction();
      break;
    case "poppies":
      if (hits.hitFloor)
        this.doPoppiesInteraction();
      break;
    case 'crow':
      if (hits.hitWall) {
        this.doCrowInteraction(this.userHitPoint);
      }
      break;
    case 'well':
      if (hits.hitFloor) {
        this.doWellInteraction(this.userHitPointFloor);
      }
      break;
    case 'stream':
      if (hits.hitWall) {
        this.doCloudInteraction(this.userHitPoint);
      }
      break;
    }

    // per scene, do an interaction

    // crow: feather fall from point
    // morning glory: ?
    // snow, heron : snow wind
    // rains : rain direction, make puddle
    // sword : ?
    // earth : ?
    // bird/sing : ?
  }

  doUserPointerUp() {
    console.log("doUserPointerUp");
    if (this.key == "cemetery") {
      this.firefliesUser = false;
      this.fireflyfriction = .9;
    }
  }

  getHitPoints(intersects) {
    if (!this.userHitPoint)
      this.userHitPoint = new Vector3();

    if (!this.userHitPointFloor)
      this.userHitPointFloor = new Vector3();

    let hitFloor = false;
    let hitWall = false;

    intersects.forEach((item, i) => {
      if (item.object.name == "wall") {
        this.userHitPoint.copy(item.point);
        hitWall = true;
      } else if (item.object.name == "floor") {
        this.userHitPointFloor.copy(item.point);
        hitFloor = true;
      }
    });

    this.scene.worldToLocal(this.userHitPoint);
    this.scene.worldToLocal(this.userHitPointFloor);

    return {hitWall : hitWall, hitFloor : hitFloor};
  }

  doFirefliesInteraction(pos) {
    this.firefliesUser = true;
    this.fireflyfriction = .99999;
    this.particles.forEach((particle, i) => {
      if (i < this.maxCreatureNum) {
        particle.target.copy(pos);
      }
    });
  }

  doSpiderInteraction(pos) {
    if (this.spiderTM)
      this.spiderTM.pause();
    this.linesMeshGroup.rotation.x = Math.degToRad(Math.random() * 20 - 10);
    this.linesMeshGroup.position.copy(pos);

    this.particles.forEach(
        (particle, i) => { particle.position.set(0, 0, 0); });

    if (this.spiderTM) {
      this.spiderTM.repeat = 0;
      this.spiderTM.restart({includeDelay : false});
    }
  }

  doPondInteraction(pos) {
    let bubbleIndex = Math.floor(Math.random() * (this.maxCreatureNum - 1));
    let particle = this.particles[bubbleIndex];
    particle.position.set(pos.x, pos.y, 0);
    particle.mesh.position.copy(particle.position);
    particle.state = "active";
    particle.mesh.scale.set(0, 0, 0);
    let newScale = .07 + Math.random() * .03;
    gsap.to(particle.mesh.scale,
            {x : newScale, y : newScale, z : newScale, duration : .25});
  }

  doRoseInteraction(pos) {
    let index = Math.floor(Math.random() * (this.maxCreatureNum - 1));
    let particle = this.particles[index];
    particle.position.set(pos.x, pos.y, -1);
    particle.mesh.position.copy(particle.position);
    particle.rotationRan = 1 + (Math.random() * .001);
    particle.mesh.material.opacity = 1;

    particle.state = "active";
  }

  doColdFlowerInteraction(pos) {
    if (!this.indexCount)
      this.indexCount = 0;
    let limit = 4 + Math.random() * 10;
    for (let i = 0; i < limit; i++) {
      let r = Math.random() * .2;
      let a = Math.random() * Math.PI * 2;
      let x = pos.x + r * Math.sin(a);
      let z = pos.z + r * Math.cos(a);
      this.indexCount++;
      let index = this.indexCount % (this.particles.length - 1);
      let particle = this.particles[index];
      particle.position.set(x, pos.y, z);
      particle.mesh.scale.set(0, 0, 0);
      particle.mesh.position.copy(particle.position);
      particle.mesh.material.opacity = 1;
      particle.state = "active";
      let newScale = .08 + Math.random() * .05;
      gsap.to(particle.mesh.scale, {
        x : newScale,
        y : newScale,
        z : newScale,
        ease : "sine.inOut",
        duration : .25 + (i * .1)
      });
    }
  }

  doStarsInteraction() {
    if (this.starsRotate == null)
      this.starsRotate = true;
    else
      this.starsRotate = !this.starsRotate;
    // let rotation =
    //     this.creatureMeshGroup.rotation.y + Math.degToRad(Math.random() *
    //     90);
    // gsap.to(this.creatureMeshGroup.rotation,
    //         {y : rotation, duration : 2, ease : "sine.inOut"});
  }

  doIrisInteraction(pos) {
    clearInterval(this.interval);
    this.addWind(new Vector3(.1, .12, 0));
  }

  doPoppiesInteraction() {
    let dir = Math.floor((Math.random() - .5) * 2);
    this.addWind(new Vector3((.1 + (Math.random() - .5) * .001) * dir, 0, 0));
    this.didWind = true;
  }

  doCrowInteraction(pos) {
    console.log("do crow interact")

    if (!this.indexCount) this.indexCount = 0;
    this.indexCount++;
    let index = this.indexCount % (this.maxCreatureNum - 1);
    let particle = this.particles[index];
    particle.position.set(pos.x, pos.y, pos.z);
    particle.mesh.position.set(pos.x, pos.y, pos.z);
    let size = .1 + (Math.random() * .2);
    particle.mesh.scale.set(size, size, size);
    particle.mesh.material.rotation = 0; // Math.random() * (Math.PI * 2);
    particle.mesh.visible = true;

    let dir = 1;
    if (pos.y < 0) {
      dir = -1;
    }
    gsap.to(
        particle.mesh.material,
        {rotation : Math.PI * 2, duration : 2, repeat : 10, ease : "linear"});
    let tm = gsap.timeline();
    tm.to(particle.mesh.material,
          {opacity : .65 + (Math.random() * .25), duration : .5});

    tm.to(particle.mesh.position, {
      x : pos.x + 2,
      y : pos.y - dir * (Math.random()),
      z : pos.z - 2,
      duration : 8
    },
          "<");
    // tm.to(particle.mesh.material,
    //       {rotation : Math.PI * 2, duration : 2, repeat : 4}, "<");
    tm.to(particle.mesh.material, {opacity : 0, duration : 1}, "<+=4.5");
  }

  doWellInteraction(pos) {
    for (let i = 0; i < this.maxCreatureNum; i++) {
      let particle = this.particles[i];
      if (particle.tm) {
        if (!particle.tm.isActive())
          particle.tm.restart();
      } else {
        let offSet = .001 + Math.random() * .01;
        particle.tm =
            gsap.timeline({delay : Math.random() * .25, repeatRefresh : true});
        particle.tm.to(
            particle.mesh.position,
            {y : particle.opos.y + offSet, duration : .5, ease : "sine.inOut"});
        particle.tm.to(particle.mesh.position, {
          y : particle.opos.y - (offSet * .5),
          duration : .5,
          ease : "sine.inOut"
        });
        particle.tm.to(
            particle.mesh.position,
            {y : particle.opos.y, duration : .5, ease : "sine.inOut"});
      }
    }
  }

  doCloudInteraction(pos) {
    console.log("doCloudInteraction");
    if (this.cloudsOn) {
      this.cloudsOn = false;
      this.particles.forEach((item, i) => {
        if (i < this.maxCreatureNum) {
          gsap.to(this.particles[i].mesh.material, {opacity : 0, duration : 2});
        }
      });

    } else {
      this.cloudsOn = true;
      this.particles.forEach((item, i) => {
        if (i < this.maxCreatureNum) {
          gsap.to(this.particles[i].mesh.material,
                  {opacity : .65, duration : 2});
        }
      });
    }
    // if (!this.indexCount)
    //   this.indexCount = 0;
    // this.indexCount++;
    // let index = this.indexCount % (this.maxCreatureNum - 1);
    // let particle = this.particles[0];
    // particle.mesh.position.set(pos.x, pos.y, pos.z);
    // let tm = gsap.timeline();
    // tm.to(particle.mesh.material, {opacity : 1, duration : 1});
    // // tm.to(particle.mesh.position, {x : 4, duration : 8}, "<");
    // // tm.to(particle.mesh.material, {opacity : 0, duration : 1});
  }
};
